import React, { useEffect, useState } from "react";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  TableContainer,
  Paper,
  Box,
  CircularProgress,
  DialogActions,
} from "@mui/material";

import {
  createData,
  deleteData,
  getData,
  updateData,
} from "../../api/ConsultingRoomApi";

import { AddCircleSharp } from "@mui/icons-material";
import {
  initialConsultingRoomData,
  ConsultingRoomData,
} from "../../types/ConsultingRoom";

import { toast } from "react-toastify";
import { showConfirmation } from "../../utils/SwalHelper";
import { hasModuleActionPermission } from "../../utils/RolePermissions";
import { AppActionEnum, ModuleEnum } from "../../utils/AppEnum";
import { GenericSearch } from "../../components/common/GenericSearch";
import { GenericTable } from "../../components/common/GenericTable";
import { tableColumns } from "./tables/TableHeader";
import ConsultingRoomForm from "./ConsultingRoomForm";
import DoctorList from "./DoctorList";
import { UserData } from "../../types/User";
import { getDoctorIdsData } from "../../api/EmployeeApi";
import { getUserListData } from "../../api/UsersApi";

const ConsultingRoomList: React.FC = () => {
  const [query, setQuery] = useState<string>("");
  const [formData, setFormData] = useState<ConsultingRoomData>();
  const [listData, setListData] = useState<ConsultingRoomData[]>([]);
  const [doctorData, setDoctorData] = useState<UserData[]>([]);
  const [selected, setSelected] = useState<ConsultingRoomData>();
  const [open, setOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [loading, setLoading] = useState(true);
  const [viewDetail, setViewDetail] = useState(false);

  const handleLoadData = async () => {
    setLoading(true);

    const ids = await getDoctorIdsData();

    const result = await getUserListData(ids);

    setDoctorData(result || []);

    setLoading(false);
  };

  const handleSearch = async () => {
    setLoading(true);
    const result = await getData();

    setListData(result || []);
    setLoading(false);
  };

  const handleAdd = () => {
    setIsEdit(false);
    setFormData(initialConsultingRoomData);
    setOpen(true);
  };

  const handleEdit = async (data: ConsultingRoomData) => {
    setIsEdit(true);

    await setFormData(data);

    setOpen(true);
  };

  const handleOnReset = async () => {
    setQuery("");
    setListData([]);
  };

  const handleDetail = async (data: ConsultingRoomData) => {
    setSelected(data);
    setViewDetail(true);
  };

  const handleDelete = async (id: number) => {
    try {
      const confirmed = await showConfirmation(
        "Are you sure?",
        "Do you want to delete this record permanently?",
        "warning",
        "Yes, delete it!"
      );

      if (confirmed) {
        const resp = await deleteData(id);
        if (!resp) {
          toast.error("Failed to delete record");
          return;
        }
        toast.success("Record deleted successfully");
        await handleSearch();
      }
    } catch (error: any) {
      console.error("Delete failed:", error);
      toast.error(error.response?.data?.message || "Error deleting record");
    }
  };

  const onSubmit = async (form: Partial<ConsultingRoomData>) => {
    setIsEdit(false);

    if (form?.id) {
      const resp = await updateData(form.id, form);
      if (resp == null) {
        toast.error("Update failed.");
        return;
      }
      setOpen(false);
      toast.success("Record successfully updated.");
    } else {
      const resp = await createData(form);

      if (resp == null) {
        toast.error("Failed to create record");
        return;
      }

      toast.success("Record successfully created.");
      setQuery(form.name ?? "");
    }
    handleSearch();
    setOpen(false);
  };

  useEffect(() => {
    handleLoadData();
  }, []);
  return (
    <div>
      {!viewDetail && (
        <Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" }, // column on mobile
              alignItems: { xs: "stretch", md: "center" },
              gap: 2,
              width: "100%",
            }}
          >
            {/* GenericSearch */}
            <Box sx={{ flex: { xs: "0 0 100%", md: "0 0 90%" } }}>
              <GenericSearch
                query={query}
                placeholder="search by Name and Description"
                fullList={true}
                setQuery={setQuery}
                onSearch={handleSearch}
                onReset={() => handleOnReset()}
              />
            </Box>

            {/* Add Button */}
            {hasModuleActionPermission(
              ModuleEnum.Registration,
              AppActionEnum.Create
            ) && (
              <Box sx={{ flex: { xs: "0 0 100%", md: "0 0 8%" } }}>
                <Button
                  fullWidth
                  startIcon={<AddCircleSharp />}
                  onClick={handleAdd}
                  variant="outlined"
                  color="success"
                >
                  Add
                </Button>
              </Box>
            )}
          </Box>

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper} sx={{ overflowX: "auto" }}>
              <GenericTable
                data={listData}
                columns={tableColumns}
                actions={{
                  edit: (row) => handleEdit(row),
                  delete: (row) => handleDelete(row.id),
                  assign: (row) => handleDetail(row),
                }}
              />
            </TableContainer>
          )}

          <Dialog
            open={open}
            onClose={(event, reason) => {
              setFormData(initialConsultingRoomData);
              if (reason === "backdropClick" || reason === "escapeKeyDown")
                return;
              setOpen(false);
            }}
            disableEscapeKeyDown
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>
              {isEdit ? "Edit Employee" : "Add Employee"}
            </DialogTitle>
            <DialogContent>
              <ConsultingRoomForm
                initialData={formData || initialConsultingRoomData}
                onSubmit={onSubmit}
                onClose={() => setOpen(false)}
              />
            </DialogContent>
          </Dialog>
        </Box>
      )}

      {viewDetail && (
        <Dialog
          open={viewDetail}
          onClose={(event, reason) => {
            setFormData(initialConsultingRoomData);
            if (reason === "backdropClick" || reason === "escapeKeyDown")
              return;
            setViewDetail(false);
          }}
          disableEscapeKeyDown
          fullWidth
          maxWidth="md"
        >
          <DialogTitle>{`Assign Doctors to ${selected?.name}`}</DialogTitle>

          <DialogContent dividers>
            <DoctorList consultingRoomId={selected?.id ?? 0} />
          </DialogContent>

          <DialogActions>
            <Button onClick={() => setViewDetail(false)}>Close</Button>
          </DialogActions>
        </Dialog>
      )}
    </div>
  );
};

export default ConsultingRoomList;
